import { useEffect, useState } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import { useAnchorEntitlements } from "@/contexts/AnchorEntitlementsContext";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import { documentBuilderSignBridgeEnabled } from "@/lib/sign/documentBuilderSignBridgeFeature";
import { createSignTemplateFromDoc } from "@/lib/sign/signTemplateApiClient";
import { turboSignEntitlementEnabled } from "@/lib/sign/turboSignEntitlement";
import { openSignTemplateEditor } from "@/lib/workspaceModeNavigation";
import { DocumentBlockReadOnlyList } from "../DocumentBlockReadOnlyList";
import { DocumentCategoryManager } from "../DocumentCategoryManager";
import { DocumentCategoryTree } from "../DocumentCategoryTree";
import { DocumentExportHistorySection } from "../editor/DocumentExportHistorySection";
import { DocumentExportPreviewModal } from "../DocumentExportPreviewModal";
import { DocumentFillModal } from "../DocumentFillModal";
import { useDocumentCategories } from "../useDocumentCategories";
import { useDocumentLibrary } from "../useDocumentLibrary";

type DocumentLibrarySurfaceProps = {
  /** Hide the page title when embedded in Documents Editor sub-mode. */
  embedded?: boolean;
  /** Editors see drafts alongside published documents. */
  includeDrafts?: boolean;
  canEdit?: boolean;
  /** Opens inline document editor (Documents Editor sub-mode). */
  onEditDocument?: (documentId: string | null) => void;
  /** Use category CRUD UI instead of read-only tree. */
  useCategoryManager?: boolean;
  /** Bump to reload library after returning from editor. */
  refreshKey?: number;
};

/** Document Builder library engine surface (browse, fill, export). */
export function DocumentLibrarySurface({
  embedded = false,
  includeDrafts = false,
  canEdit = false,
  onEditDocument,
  useCategoryManager = false,
  refreshKey = 0,
}: DocumentLibrarySurfaceProps) {
  const { anchors } = useAnchorEntitlements();
  const turboSignEnabled = turboSignEntitlementEnabled(anchors);
  const categories = useDocumentCategories(true);
  const library = useDocumentLibrary(categories.selectedCategoryId, { includeDrafts });
  const signBridgeEnabled = documentBuilderSignBridgeEnabled() && turboSignEnabled;
  const [signBridgeBusy, setSignBridgeBusy] = useState(false);
  const [signBridgeError, setSignBridgeError] = useState<string | null>(null);
  const [exportHistoryRefresh, setExportHistoryRefresh] = useState(0);

  useEffect(() => {
    if (refreshKey > 0) void library.reload();
  }, [refreshKey, library.reload]);

  const primaryActionLabel = library.selectedDocumentId ? "Edit document" : "Add new";

  async function handleSendForSignature() {
    const doc = library.selectedDocument;
    const versionId = library.publishedVersionId;
    if (!doc || !versionId) return;

    setSignBridgeBusy(true);
    setSignBridgeError(null);
    const r = await createSignTemplateFromDoc({
      document_id: doc.id,
      version_id: versionId,
    });
    setSignBridgeBusy(false);
    if (!r.ok) {
      setSignBridgeError(r.error);
      return;
    }
    openSignTemplateEditor(r.data.version.id);
  }

  const selectedName =
    categories.flatCategories.find((c) => c.id === categories.selectedCategoryId)?.name ??
    "All categories";

  const emptyTitle = includeDrafts ? "No documents yet" : "No published documents";
  const listDescription = canEdit ? "Documents in this folder" : "Published documents";

  return (
    <div {...{ [FIREFLY_TARGET_ATTR]: "doc-library-surface" }} className="space-y-5">
      {embedded ? null : (
        <header className="space-y-1">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-zinc-100">Documents</h2>
          <p className="text-sm text-gray-600 dark:text-zinc-300">
            Browse published documents, fill template variables, preview the PDF, download, or register
            an export in storage.
          </p>
        </header>
      )}

      {canEdit && onEditDocument ? (
        <div className="flex flex-wrap items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => onEditDocument(library.selectedDocumentId)}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
          >
            {primaryActionLabel}
          </button>
        </div>
      ) : null}

      {categories.error || library.error || signBridgeError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-200">
          {categories.error ?? library.error ?? signBridgeError}
        </div>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[minmax(220px,280px)_1fr]">
        <SectionCard
          title="Categories"
          description={
            categories.seededDefaults
              ? "Default folders were added for this organization."
              : useCategoryManager
                ? "Select a folder to browse documents."
                : "Select a folder to browse published documents."
          }
        >
          {categories.loading ? (
            <p className="text-sm text-gray-500 dark:text-zinc-400">Loading categories…</p>
          ) : useCategoryManager ? (
            <DocumentCategoryManager
              tree={categories.tree}
              flatCategories={categories.flatCategories}
              selectedId={categories.selectedCategoryId}
              onSelect={categories.setSelectedCategoryId}
              onChanged={() => void categories.reload()}
              loading={categories.loading}
            />
          ) : (
            <DocumentCategoryTree
              nodes={categories.tree}
              selectedId={categories.selectedCategoryId}
              onSelect={categories.setSelectedCategoryId}
            />
          )}
        </SectionCard>

        <SectionCard title={selectedName} description={listDescription}>
          {library.loading ? (
            <p className="text-sm text-gray-500 dark:text-zinc-400">Loading documents…</p>
          ) : library.documents.length === 0 ? (
            <div className="flex min-h-[180px] flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50/60 px-6 py-8 text-center dark:border-zinc-700 dark:bg-zinc-900/40">
              <p className="text-sm font-medium text-gray-800 dark:text-zinc-200">{emptyTitle}</p>
              <p className="mt-2 max-w-md text-sm text-gray-600 dark:text-zinc-400">
                {categories.selectedCategoryId
                  ? includeDrafts
                    ? `Nothing in ${selectedName} yet.${canEdit ? " Use Add new to create one." : ""}`
                    : `Nothing published in ${selectedName} yet. Authors publish from Document editor.`
                  : "Choose a category to browse documents."}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <ul className="space-y-2">
                {library.documents.map((doc) => {
                  const selected = library.selectedDocumentId === doc.id;
                  return (
                    <li key={doc.id}>
                      <button
                        type="button"
                        onClick={() => library.setSelectedDocumentId(doc.id)}
                        className={[
                          "w-full rounded-lg border px-3 py-2 text-left transition",
                          selected
                            ? "border-indigo-500 bg-indigo-50 dark:border-indigo-400 dark:bg-indigo-950/40"
                            : "border-gray-200 hover:border-gray-300 dark:border-zinc-700 dark:hover:border-zinc-600",
                        ].join(" ")}
                      >
                        <p className="text-sm font-medium text-gray-900 dark:text-zinc-100">
                          {doc.name}
                        </p>
                        {doc.description ? (
                          <p className="mt-0.5 text-xs text-gray-600 dark:text-zinc-400">
                            {doc.description}
                          </p>
                        ) : null}
                        {doc.tags.length > 0 ? (
                          <p className="mt-1 flex flex-wrap gap-1">
                            {doc.tags.slice(0, 4).map((tag) => (
                              <span
                                key={tag}
                                className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600 dark:bg-zinc-800 dark:text-zinc-300"
                              >
                                {tag}
                              </span>
                            ))}
                          </p>
                        ) : null}
                        {doc.published_version_number ? (
                          <p className="mt-1 text-[10px] uppercase tracking-wide text-gray-500 dark:text-zinc-500">
                            Published v{doc.published_version_number}
                          </p>
                        ) : canEdit ? (
                          <p className="mt-1 text-[10px] uppercase tracking-wide text-amber-600 dark:text-amber-400">
                            Draft
                          </p>
                        ) : null}
                      </button>
                    </li>
                  );
                })}
              </ul>

              {library.selectedDocument?.published_version_id ? (
                <div className="rounded-lg border border-gray-200 p-4 dark:border-zinc-700">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">
                        {library.selectedDocument.name}
                        {library.selectedDocument.published_version_number
                          ? ` — v${library.selectedDocument.published_version_number}`
                          : ""}
                      </h3>
                      <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
                        Template preview — variables show as {"{{placeholders}}"}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={library.viewerLoading}
                        onClick={library.openFillModal}
                        className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                      >
                        Fill &amp; export
                      </button>
                      {signBridgeEnabled ? (
                        <button
                          type="button"
                          disabled={library.viewerLoading || signBridgeBusy || !library.publishedVersionId}
                          onClick={() => void handleSendForSignature()}
                          className="rounded-md border border-indigo-300 bg-white px-3 py-1.5 text-sm font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50 dark:border-indigo-700 dark:bg-zinc-900 dark:text-indigo-300 dark:hover:bg-indigo-950/40"
                        >
                          {signBridgeBusy ? "Opening…" : "Send for signature"}
                        </button>
                      ) : null}
                    </div>
                  </div>
                  {library.viewerLoading ? (
                    <p className="mt-3 text-sm text-gray-500 dark:text-zinc-400">Loading snapshot…</p>
                  ) : (
                    <div className="mt-3">
                      <DocumentBlockReadOnlyList
                        blocks={library.publishedBlocks}
                        assets={library.assets}
                      />
                    </div>
                  )}

                  <div className="mt-4 border-t border-gray-200 pt-4 dark:border-zinc-700">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
                      Export history
                    </h4>
                    <div className="mt-2">
                      <DocumentExportHistorySection
                        documentId={library.selectedDocument.id}
                        versionId={library.publishedVersionId}
                        refreshKey={exportHistoryRefresh + refreshKey}
                      />
                    </div>
                  </div>
                </div>
              ) : library.selectedDocument && canEdit ? (
                <p className="text-sm text-gray-600 dark:text-zinc-300">
                  This document has no published version yet. Use Edit document to author and publish.
                </p>
              ) : null}
            </div>
          )}
        </SectionCard>
      </div>

      {library.selectedDocument?.published_version_id ? (
        <>
          <DocumentFillModal
            open={library.fillModalOpen}
            documentName={library.selectedDocument.name}
            variables={library.publishedVariables}
            blocks={library.publishedBlocks}
            initialValues={library.fillValues}
            onClose={library.closeFillModal}
            onProceed={library.completeFill}
          />
          <DocumentExportPreviewModal
            open={library.exportPreviewOpen}
            documentId={library.selectedDocument.id}
            versionId={library.publishedVersionId ?? ""}
            documentName={library.selectedDocument.name}
            versionNumber={library.publishedVersionNumber ?? 0}
            blocks={library.publishedBlocks}
            assets={library.assets}
            filledValues={library.fillValues}
            onClose={library.closeExportPreview}
            onExportRegistered={() => setExportHistoryRefresh((n) => n + 1)}
          />
        </>
      ) : null}
    </div>
  );
}
